import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Button,
  CircularProgress,
  Chip,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid
} from '@mui/material';
import surveyService from '../../services/surveyService';

const AllSurveyWindows = () => {
  const [windowsData, setWindowsData] = useState({ windows: [], pagination: { count: 0, totalPages: 1 } });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const fetchSurveyWindows = async () => {
    try {
      setLoading(true);
      // Call API with pagination and filter options
      const data = await surveyService.getAllSurveyWindows({
        page,
        pageSize,
        status: statusFilter,
        search: searchTerm
      });
      setWindowsData(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching survey windows:', err);
      setError('Failed to load survey windows. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch data when pagination, filters or search changes
  useEffect(() => {
    fetchSurveyWindows();
  }, [page, pageSize, statusFilter]);
  
  // Handle page change
  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  // Handle page size change
  const handlePageSizeChange = (event) => {
    setPageSize(event.target.value);
    setPage(1); // Reset to first page when changing page size
  };

  // Handle status filter change
  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
    setPage(1); // Reset to first page when changing filter
  };
  
  // Handle search
  const handleSearch = (event) => {
    // Only update search when Enter key is pressed
    if (event.key === 'Enter') {
      setPage(1); // Reset to first page when searching
      fetchSurveyWindows();
    }
  };
  
  // Handle click search button
  const handleSearchClick = () => {
    setPage(1); // Reset to first page when searching
    fetchSurveyWindows();
  };

  // Helper function to get status color
  const getStatusColor = (status) => {
    switch(status?.toLowerCase()) {
      case 'open':
        return 'success';
      case 'in_progress':
      case 'pending':
        return 'warning';
      case 'completed':
        return 'primary';
      case 'expired':
      case 'missed':
        return 'error';
      case 'upcoming':
      case 'closed':
        return 'info';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error" variant="h6">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', p: 2 }}>
      <Typography variant="h5" gutterBottom component="div" sx={{ mb: 3 }}>
        All Survey Windows
      </Typography>
      
      {/* Filters and Search */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <FormControl fullWidth>
            <InputLabel id="status-filter-label">Status</InputLabel>
            <Select
              labelId="status-filter-label"
              value={statusFilter}
              label="Status"
              onChange={handleStatusFilterChange}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="open">Open</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="missed">Missed</MenuItem>
              <MenuItem value="closed">Closed</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={5}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
            <TextField
              fullWidth
              label="Search"
              variant="outlined"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleSearch}
              placeholder="Search by survey, participant ID, email, study"
              helperText="Use full UUID for exact participant matching"
              sx={{ mr: 1 }}
            />
            <Button 
              variant="contained" 
              color="primary" 
              onClick={handleSearchClick}
              sx={{ mt: 1, height: 40 }}
            >
              Search
            </Button>
          </Box>
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControl sx={{ minWidth: 120 }}>
            <InputLabel id="page-size-label">Items per page</InputLabel>
            <Select
              labelId="page-size-label"
              value={pageSize}
              label="Items per page"
              onChange={handlePageSizeChange}
            >
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {/* Group windows by visit_number */}
      {(() => {
        // Group windows by visit_number
        const grouped = {};
        (windowsData.windows || []).forEach((window) => {
          const visitNum = window.visit_number || 'Ungrouped';
          if (!grouped[visitNum]) grouped[visitNum] = [];
          grouped[visitNum].push(window);
        });
        const visitNumbers = Object.keys(grouped).sort((a, b) => {
          if (a === 'Ungrouped') return 1;
          if (b === 'Ungrouped') return -1;
          return Number(a) - Number(b);
        });
        if (visitNumbers.length === 0) {
          return <Typography variant="body1">No survey windows found.</Typography>;
        }
        return visitNumbers.map((visitNum) => (
          <Box key={visitNum} sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {visitNum === 'Ungrouped' ? 'Ungrouped Windows' : `Visit #${visitNum}`}
            </Typography>
            <TableContainer component={Paper} sx={{ boxShadow: 2, mb: 2 }}>
              <Table sx={{ minWidth: 650 }} aria-label={`survey windows table for visit ${visitNum}`}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'primary.light' }}>
                    <TableCell>ID</TableCell>
                    <TableCell>Survey Name</TableCell>
                    <TableCell>Participant</TableCell>
                    <TableCell>Study</TableCell>
                    <TableCell>Start Date</TableCell>
                    <TableCell>End Date</TableCell>
                    <TableCell>Completion Date</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Activated</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {grouped[visitNum].map((window) => (
                    <TableRow
                      key={window.id}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <TableCell component="th" scope="row">{window.id}</TableCell>
                      <TableCell>
                        <Typography variant="body2" component="div">
                          {window.surveyName && !window.surveyName.startsWith('Survey #') 
                            ? window.surveyName 
                            : 'Survey Name Not Available'}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          ID: {window.surveyId || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" component="div">
                          {window.participantId || 'N/A'}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {window.participantName}
                        </Typography>
                      </TableCell>
                      <TableCell>{window.studyName}</TableCell>
                      <TableCell>{window.startDate}</TableCell>
                      <TableCell>{window.endDate}</TableCell>
                      <TableCell>{window.completionDate}</TableCell>
                      <TableCell>
                        <Chip
                          label={window.status}
                          color={getStatusColor(window.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={window.isActivated ? 'Yes' : 'No'}
                          color={window.isActivated ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        ));
      })()}

      {/* Pagination controls */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
        <Typography variant="body2" sx={{ mr: 2, alignSelf: 'center' }}>
          Showing {windowsData.windows.length} of {windowsData.pagination.count} items
        </Typography>
        <Pagination
          count={windowsData.pagination.totalPages}
          page={page}
          onChange={handlePageChange}
          color="primary"
          showFirstButton
          showLastButton
        />
      </Box>
    </Box>
  );
};

export default AllSurveyWindows;